import { ArrowRight, BookOpen, CircleDot, Gauge, Ruler, Search, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

import usePageMetadata from '../../hooks/usePageMetadata';
import { useUnits } from '../../units/UnitContext';
import { formatUnitValue, unitLabel } from '../../units/unitConversions';
import './TolerancesHome.css';

const TolerancesHome = () => {
  const { unitSystem } = useUnits();
  usePageMetadata(
    'Dimensional Tolerance Tools for Machining | ASiJoLab',
    'Practical ISO 286 tools for calculating limit deviations and evaluating clearance, transition, and interference fits.'
  );

  return (
    <div className="tolerancesHome">
      <header className="tolerancesHomeIntro">
        <p>Dimensional tolerance tools</p>
        <h1>Dimensional tolerances for machining</h1>
        <span>
          Practical tools for interpreting dimensional tolerances used in
          machining and manufacturing.
        </span>
      </header>

      <section className="tolerancesTools" aria-labelledby="tolerances-tools-title">
        <div>
          <p>Available now</p>
          <h2 id="tolerances-tools-title">Tolerance tools</h2>
        </div>

        <div className="tolerancesToolGrid">
          <Link className="tolerancesToolCard" to="/tolerances/limit-deviations">
          <span className="tolerancesToolIcon" aria-hidden="true">
            <Ruler size={28} strokeWidth={1.9} />
          </span>
          <span className="tolerancesToolCopy">
            <strong>Limit Deviations</strong>
            <span>
              Find ISO 286 upper and lower deviations and dimensional limits
              from a designation such as 25H7 or 25h7.
            </span>
          </span>
          <span className="tolerancesToolAction">
            Open calculator
            <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
          </span>
          </Link>

          <Link className="tolerancesToolCard" to="/tolerances/fits">
            <span className="tolerancesToolIcon" aria-hidden="true">
              <CircleDot size={28} strokeWidth={1.9} />
            </span>
            <span className="tolerancesToolCopy">
              <strong>Fit Calculator</strong>
              <span>
                Evaluate a known hole-and-shaft combination, including clearance,
                transition or interference, dimensional limits, and tolerance zones.
              </span>
            </span>
            <span className="tolerancesToolAction">
              Open fit calculator
              <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
            </span>
          </Link>

          <Link className="tolerancesToolCard" to="/tolerances/fit-finder">
            <span className="tolerancesToolIcon" aria-hidden="true">
              <Search size={28} strokeWidth={1.9} />
            </span>
            <span className="tolerancesToolCopy">
              <strong>Fit Finder</strong>
              <span>
                Enter a required clearance or interference range and search verified
                ISO 286 combinations, with preferred and common fits prioritized.
              </span>
            </span>
            <span className="tolerancesToolAction">
              Find a fit
              <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
            </span>
          </Link>

          <Link className="tolerancesToolCard" to="/tolerances/recommended-fits">
            <span className="tolerancesToolIcon" aria-hidden="true">
              <BookOpen size={28} strokeWidth={1.9} />
            </span>
            <span className="tolerancesToolCopy">
              <strong>Recommended Fits</strong>
              <span>
                Browse curated commonly used hole-basis and shaft-basis combinations
                across clearance, transition, and interference categories.
              </span>
            </span>
            <span className="tolerancesToolAction">
              Browse common fits
              <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
            </span>
          </Link>

          <Link className="tolerancesToolCard" to="/tolerances/gauges">
            <span className="tolerancesToolIcon" aria-hidden="true">
              <Gauge size={28} strokeWidth={1.9} />
            </span>
            <span className="tolerancesToolCopy">
              <strong>Gauge Calculator</strong>
              <span>
                Calculate DIN 7162 GO and NO-GO gauge dimensions and understand
                how manufacturing tolerance and wear relate to the workpiece tolerance.
              </span>
            </span>
            <span className="tolerancesToolAction">
              Open Gauge Calculator
              <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
            </span>
          </Link>

          <Link className="tolerancesToolCard" to="/tolerances/reamer-calculator">
            <span className="tolerancesToolIcon" aria-hidden="true">
              <Settings size={28} strokeWidth={1.9} />
            </span>
            <span className="tolerancesToolCopy">
              <strong>Reamer Calculator</strong>
              <span>
                Calculate manufacturing dimensions and pre-drill recommendations
                for solid carbide reamers up to Ø{formatUnitValue(20, 'mm', unitSystem)} {unitLabel('mm', unitSystem)}.
              </span>
            </span>
            <span className="tolerancesToolAction">
              Calculate reamer
              <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TolerancesHome;
